import { useState } from 'react';
import { isEmpty } from 'lodash';
import { useMutation, useQueryClient } from '@tanstack/react-query';
//
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
import { AddressInfo, SectionLabel } from '../..';
import { Iconify } from '@/components';
import { TableRowLink } from '@/components/table';
import { CUSTOM_CHAT_ENABLED } from '@/config';
import {
  openCustomDashboardChatRequest,
  openStreamDashboardChatRequest,
} from '@/features/custom-chat';
// redux
import { useSelector } from '@/stores';
// hooks + utils
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useResponsive from '@/hooks/useResponsive';
import useSettings from '@/hooks/useSettings';
import { useGetAccess } from '@/hooks/useAccess';
import { getDefaultClientAddress, tableRowLinkSx } from '../../../utils';
import { providersPath } from '@/features/users/providers';
import { sendBookingPaymentMethodRequest } from '@/features/bookings-management/api';
// types
import { UserAddress } from '@/features/users/shared/types';
import { Booking } from '@/features/bookings-management/types';

type ViewBookingHeaderProps = {
  currentBooking?: Booking;
};

export default function ViewBookingHeader({
  currentBooking,
}: ViewBookingHeaderProps) {
  const { themeStretch } = useSettings();
  const upMd = useResponsive('up', 'md');
  const booking = useSelector((state) => state.booking);
  const queryClient = useQueryClient();
  const { successMsg, errorMsg } = useSnackbarMsg();
  const chatAccess = useGetAccess('chat');
  const [
    isPaymentMethodRequestDialogOpen,
    setPaymentMethodRequestDialogOpen,
  ] = useState(false);

  const cleaner = !!booking.booking_cleaners.length
    ? booking.booking_cleaners[0]
    : null;
  const cleanerName = cleaner
    ? `${cleaner.first_name ?? 'N/A'} ${cleaner.last_name ?? 'N/A'}`
    : 'N/A';
  const serviceLabelText = `${
    booking.service_variant?.service.name ?? 'N/A'
  } - ${booking.service_variant?.name ?? 'N/A'}`;
  const bookingId = Number(currentBooking?.id ?? booking.booking_id);
  const hasAttachedPaymentMethod = Boolean(
    currentBooking?.payment_method ||
      currentBooking?.payment_method_details ||
      currentBooking?.wallet
  );

  const openChatRequest = (
    payload:
      | { client_user_id: number; type: 'clientSupport' }
      | { cleaner_user_id: number; type: 'cleanerSupport' },
    requestIdPrefix: string,
    requestIdSource: number | string,
    custom = false
  ) => {
    const openRequest = custom
      ? openCustomDashboardChatRequest
      : openStreamDashboardChatRequest;

    openRequest(payload, requestIdPrefix, requestIdSource);
  };

  const handleChatClient = (custom = false) => {
    if (!booking.client?.id) {
      errorMsg(
        new Error('No linked client was found for this booking.'),
        'Unable to open client chat'
      );
      return;
    }

    openChatRequest(
      {
        client_user_id: Number(booking.client.id),
        type: 'clientSupport',
      },
      custom ? 'custom_booking_client_chat' : 'booking_client_chat',
      booking.client.id,
      custom
    );
  };

  const handleChatCleaner = (custom = false) => {
    if (!cleaner?.id) {
      errorMsg(
        new Error('No assigned cleaner was found for this booking.'),
        'Unable to open cleaner chat'
      );
      return;
    }

    openChatRequest(
      {
        cleaner_user_id: Number(cleaner.id),
        type: 'cleanerSupport',
      },
      custom ? 'custom_booking_cleaner_chat' : 'booking_cleaner_chat',
      cleaner.id,
      custom
    );
  };

  const sendPaymentMethodRequestMutation = useMutation({
    mutationFn: () => {
      if (!bookingId) throw new Error('Missing booking id');
      return sendBookingPaymentMethodRequest(bookingId);
    },
    onSuccess: () => {
      successMsg('Payment method email sent');
      setPaymentMethodRequestDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['bookings'], exact: false });
      queryClient.invalidateQueries({
        queryKey: ['bookingSessions'],
        exact: false,
      });
    },
    onError: (error) =>
      errorMsg(error, 'Failed to send payment method email'),
  });

  const handleClosePaymentMethodRequestDialog = () => {
    if (sendPaymentMethodRequestMutation.isLoading) return;
    setPaymentMethodRequestDialogOpen(false);
  };

  return (
    <Card sx={{ px: themeStretch ? 5 : 3, py: 3 }}>
      <Stack
        spacing={{ xs: 2, md: 5 }}
        direction={{ xs: 'column', md: 'row' }}
        divider={
          <Divider
            flexItem
            orientation={upMd ? 'vertical' : 'horizontal'}
            sx={{ borderStyle: 'dashed' }}
          />
        }
      >
        {/* CLIENT */}
        <Stack sx={{ width: 1 }}>
          <SectionLabel label="Client:" sx={{ pb: 1 }} />

          {!isEmpty(booking.client) && (
            <AddressInfo
              name={`${booking.client.first_name} ${booking.client.last_name}`}
              address={getDefaultClientAddress(booking)}
              phone={booking.client.phone}
              clientId={booking.client.id}
              hideGoogleMapsButton={!!!getDefaultClientAddress(booking)}
            />
          )}

          <Button
            variant="text"
            size="small"
            sx={{ width: 'fit-content' }}
            startIcon={<Iconify icon="eva:message-circle-outline" width={15} />}
            disabled={!chatAccess.canView}
            onClick={() => handleChatClient(false)}
          >
            Chat Client
          </Button>
          {CUSTOM_CHAT_ENABLED && (
            <Button
              variant="text"
              size="small"
              sx={{ width: 'fit-content' }}
              startIcon={
                <Iconify icon="eva:message-square-outline" width={15} />
              }
              disabled={!chatAccess.canView}
              onClick={() => handleChatClient(true)}
            >
              Custom Chat Client
            </Button>
          )}
        </Stack>

        {/* ADDRESS */}
        <Stack sx={{ width: 1 }}>
          <SectionLabel label="Address:" sx={{ pb: 1 }} />
          {!isEmpty(booking.address) && (
            <AddressInfo
              name={(booking.address as UserAddress).name}
              address={(booking.address as UserAddress).formatted_address}
              postcode={(booking.address as UserAddress).postcode}
              serviceLabelText={serviceLabelText}
            />
          )}

          {!hasAttachedPaymentMethod && Boolean(bookingId) && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              <SectionLabel label="Payment Method:" />
              <Typography variant="body2" color="text.secondary">
                No payment method set
              </Typography>
              <Button
                variant="outlined"
                size="small"
                color="success"
                sx={(theme) => ({
                  width: 'fit-content',
                  minHeight: 36,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  borderColor: alpha(theme.palette.success.main, 0.32),
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  boxShadow: `0 1px 0 ${alpha(
                    theme.palette.success.main,
                    0.16
                  )}`,
                  color: theme.palette.success.dark,
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: 1.2,
                  textTransform: 'none',
                  '& .MuiButton-startIcon': {
                    ml: -0.25,
                    mr: 0.75,
                    color: theme.palette.success.main,
                  },
                  '&:hover': {
                    borderColor: theme.palette.success.main,
                    bgcolor: alpha(theme.palette.success.main, 0.14),
                    boxShadow: `0 2px 6px ${alpha(
                      theme.palette.success.main,
                      0.18
                    )}`,
                  },
                })}
                startIcon={
                  <Iconify
                    icon="material-symbols:add-card-outline"
                    width={16}
                  />
                }
                onClick={() => setPaymentMethodRequestDialogOpen(true)}
              >
                Request payment method
              </Button>
            </Stack>
          )}
        </Stack>

        {/* CLEANER */}
        <Stack sx={{ width: 1 }}>
          <SectionLabel label="Cleaner:" sx={{ pb: 1 }} />
          <TableRowLink
            linkText={cleanerName}
            linkTo={providersPath('edit', {
              userId: String(cleaner?.id ?? 0),
              tabSid: 'details',
            })}
            noWrap
            sx={tableRowLinkSx}
          />

          <Button
            variant="text"
            size="small"
            sx={{ width: 'fit-content' }}
            startIcon={<Iconify icon="eva:message-circle-outline" width={15} />}
            disabled={!chatAccess.canView}
            onClick={() => handleChatCleaner(false)}
          >
            Chat Cleaner
          </Button>
          {CUSTOM_CHAT_ENABLED && (
            <Button
              variant="text"
              size="small"
              sx={{ width: 'fit-content' }}
              startIcon={
                <Iconify icon="eva:message-square-outline" width={15} />
              }
              disabled={!chatAccess.canView}
              onClick={() => handleChatCleaner(true)}
            >
              Custom Chat Cleaner
            </Button>
          )}
        </Stack>
      </Stack>

      <Dialog
        fullWidth
        maxWidth="xs"
        open={isPaymentMethodRequestDialogOpen}
        onClose={handleClosePaymentMethodRequestDialog}
      >
        <DialogTitle>Send payment method request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            The client will receive an email with a link to add a payment
            method to this booking.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePaymentMethodRequestDialog}
            disabled={sendPaymentMethodRequestMutation.isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={() => sendPaymentMethodRequestMutation.mutate()}
            loading={sendPaymentMethodRequestMutation.isLoading}
            disabled={!bookingId}
          >
            Send payment method email
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
